/**
 * File chunking utilities for multipart uploads
 */

/**
 * Default chunk size (5MB) - this is the minimum for S3 multipart
 */
export const DEFAULT_CHUNK_SIZE = 5 * 1024 * 1024;

/**
 * Threshold for multipart uploads (10MB)
 * Files larger than this will use multipart strategy
 */
export const MULTIPART_THRESHOLD = 10 * 1024 * 1024;

/**
 * Maximum concurrent chunk uploads
 */
export const MAX_CONCURRENT_CHUNKS = 3;

/**
 * Determines the upload strategy based on file size
 */
export function determineUploadStrategy(fileSize: number): 'post' | 'multipart' {
	return fileSize > MULTIPART_THRESHOLD ? 'multipart' : 'post';
}

/**
 * Calculates the number of parts needed for multipart upload
 */
export function calculateParts(fileSize: number, chunkSize: number = DEFAULT_CHUNK_SIZE): number {
	return Math.ceil(fileSize / chunkSize);
}

/**
 * Splits a file into chunks for multipart upload
 */
export function splitIntoChunks(file: File, chunkSize: number = DEFAULT_CHUNK_SIZE): Blob[] {
	const chunks: Blob[] = [];
	let offset = 0;

	while (offset < file.size) {
		const end = Math.min(offset + chunkSize, file.size);
		chunks.push(file.slice(offset, end));
		offset = end;
	}

	return chunks;
}

/**
 * Gets chunk boundaries for a specific part number
 */
export function getChunkBoundaries(
	fileSize: number,
	partNumber: number,
	chunkSize: number = DEFAULT_CHUNK_SIZE
): { start: number; end: number } {
	const start = (partNumber - 1) * chunkSize;
	const end = Math.min(start + chunkSize, fileSize);
	return { start, end };
}

/**
 * Calculates SHA256 checksum for a chunk using Web Crypto API
 */
export async function calculateChunkChecksum(chunk: ArrayBuffer): Promise<string> {
	const hashBuffer = await crypto.subtle.digest('SHA-256', chunk);
	const hashArray = new Uint8Array(hashBuffer);
	return btoa(String.fromCharCode(...hashArray));
}

/**
 * Reads a file chunk as ArrayBuffer
 */
export function readChunkAsArrayBuffer(chunk: Blob): Promise<ArrayBuffer> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result as ArrayBuffer);
		reader.onerror = () => reject(new Error('Failed to read chunk'));
		reader.readAsArrayBuffer(chunk);
	});
}

/**
 * Formats file size for display
 */
export function formatFileSize(bytes: number): string {
	if (bytes === 0) return '0 Bytes';

	const k = 1024;
	const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
	const i = Math.floor(Math.log(bytes) / Math.log(k));

	return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Calculates upload progress percentage
 */
export function calculateProgress(bytesUploaded: number, totalBytes: number): number {
	if (totalBytes === 0) return 0;
	return Math.round((bytesUploaded / totalBytes) * 100);
}
